import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { GraduationCap, Briefcase, Palette, ArrowRight, CheckCircle, FileText, Users, Lightbulb, Sparkles } from "lucide-react";
import Marquee from "react-fast-marquee";

const workshops = [
  {
    id: "cash-for-class", name: "Cash for Class", subtitle: "Scholarship Workshop", icon: GraduationCap,
    description: "Empowers high school seniors in Kern County to apply for scholarships through Kern Community Foundation and others available to them, increasing access to higher education.",
    tags: ["Education", "Scholarships", "College Prep"],
    deliverables: ["Personalized scholarship guidance", "Future college-bound leaders", "Additional scholarship connections"],
    image: "https://customer-assets.emergentagent.com/job_98cd7b09-1d25-49bf-8206-01ee668f439d/artifacts/exd3vft2_Screenshot%202026-01-23%20091158.png"
  },
  {
    id: "resume-interview", name: "Resume Building & Interview Prep", subtitle: "Career Readiness Workshop", icon: Briefcase,
    description: "Helps youth craft strong resumes and sharpen interview skills. Through hands-on guidance and mock interviews, youth learn to showcase their strengths.",
    tags: ["Career", "Job Ready", "Professional"],
    deliverables: ["Personalized resume feedback", "Real-world preparation", "Interview confidence"],
    image: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/vqq3edy8_IMG_9560.jpeg"
  },
  {
    id: "create-your-lane", name: "Create Your Lane", subtitle: "Entrepreneurship & Design", icon: Palette,
    description: "Learn creative and entrepreneurial skills to build your own brand and business. Get hands-on experience with professional tools.",
    tags: ["Creative", "Entrepreneurship", "Branding"],
    deliverables: ["Canva design skills", "Cricut & Heat Press", "Brand development"],
    image: "https://customer-assets.emergentagent.com/job_soaryouth/artifacts/lp0f2vc0_IMG_9689.jpeg"
  }
];

const Workshops = () => {
  useEffect(() => {
    document.title = "Workshops | Soar Solutions Youth Center";
  }, []);

  return (
    <div data-testid="workshops-page" className="bg-white">

      {/* Hero */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_98cd7b09-1d25-49bf-8206-01ee668f439d/artifacts/9wscvoai_Screenshot%202026-01-23%20090126.png"
            alt="Workshops"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-soar-blue via-soar-blue/90 to-soar-blue/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="max-w-3xl space-y-6">
            <span className="inline-block border border-soar-gold/40 rounded-full px-4 py-1.5 text-xs font-semibold tracking-widest text-soar-gold uppercase opacity-0 animate-fade-up" style={{ animationFillMode: 'forwards' }}>
              Skill-Building Workshops
            </span>
            <h1 className="font-heading text-6xl sm:text-7xl lg:text-8xl leading-none tracking-tight text-white opacity-0 animate-fade-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
              Our <span className="text-soar-gold">Workshops</span>
            </h1>
            <p className="text-xl text-white/80 max-w-2xl opacity-0 animate-fade-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
              Practical, hands-on learning experiences designed to equip youth with real-world skills.
              From scholarships to careers to creativity—we help you build the skills to succeed.
            </p>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="py-5" style={{ backgroundColor: '#212163' }}>
        <Marquee speed={50} gradient={false}>
          {["LEARN", "GROW", "CREATE", "BUILD", "SUCCEED", "THRIVE"].map((w) => (
            <span key={w} className="font-heading text-4xl mx-12 tracking-wider" style={{ color: 'rgba(221,182,77,0.3)' }}>{w}</span>
          ))}
        </Marquee>
      </div>

      {/* Workshop Cards */}
      <section className="py-24 bg-white" data-testid="workshops-grid">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            {workshops.map((ws) => (
              <div key={ws.id} className="card-elevated rounded-3xl overflow-hidden group">
                <div className="relative h-56 overflow-hidden">
                  <img src={ws.image} alt={ws.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-soar-blue/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex gap-2 mb-2 flex-wrap">
                      {ws.tags.map(tag => (<span key={tag} className="text-[10px] bg-soar-teal/80 text-white px-2 py-0.5 rounded-full font-semibold">{tag}</span>))}
                    </div>
                    <h3 className="font-heading text-2xl text-white tracking-tight">{ws.name}</h3>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-soar-blue/50 text-sm mb-4">{ws.description}</p>
                  <div className="space-y-2 mb-5">
                    {ws.deliverables.map((item) => (
                      <div key={item} className="flex items-start gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-soar-teal flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="text-xs text-soar-blue/50">{item}</span>
                      </div>
                    ))}
                  </div>
                  <Link to={`/register?event=${ws.id}&title=${encodeURIComponent(ws.name)}`}>
                    <Button className="w-full rounded-full bg-soar-teal hover:bg-soar-teal/90 font-bold hover:scale-[1.02] transition-transform duration-200">
                      Sign Up <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Sections */}
      <section className="py-24 bg-white" data-testid="workshop-details">
        <div className="max-w-7xl mx-auto px-6 space-y-32">

          {/* Cash for Class */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-soar-teal/10 flex items-center justify-center">
                  <GraduationCap className="w-8 h-8 text-soar-teal" aria-hidden="true" />
                </div>
                <div>
                  <h2 className="font-heading text-4xl text-soar-blue tracking-tight">Cash for Class</h2>
                  <p className="font-accent text-2xl text-soar-gold">Scholarship Workshop</p>
                </div>
              </div>
              <p className="text-lg text-soar-blue/60 leading-relaxed">
                Empowers high school seniors in Kern County to apply for scholarships through Kern Community
                Foundation and others available to them, increasing access to higher education.
              </p>
              <div className="card-elevated rounded-2xl p-6">
                <h4 className="font-heading text-soar-blue mb-4 text-sm">What You'll Gain:</h4>
                <ul className="space-y-3">
                  {["Personalized scholarship application guidance", "Become a future college-bound leader", "Connections to additional scholarship opportunities"].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-soar-teal flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="text-soar-blue/50 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Link to="/register?event=evt-cash-for-class&title=Cash%20for%20Class">
                <Button className="rounded-full bg-soar-teal hover:bg-soar-teal/90 px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                  Sign Up <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img src="https://customer-assets.emergentagent.com/job_98cd7b09-1d25-49bf-8206-01ee668f439d/artifacts/exd3vft2_Screenshot%202026-01-23%20091158.png" alt="Cash for Class" className="rounded-3xl w-full" />
              <div className="absolute -bottom-5 -left-5 bg-soar-gold text-[#0a0a2e] rounded-2xl p-5 shadow-lg">
                <p className="font-heading text-3xl">10</p>
                <p className="text-sm font-medium">Youth to College</p>
              </div>
            </div>
          </div>

          {/* Resume Building */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <img src="https://customer-assets.emergentagent.com/job_soaryouth/artifacts/dajl2tg4_IMG_9561.jpeg" alt="Resume Building" className="rounded-3xl w-full" />
              <div className="absolute -bottom-5 -right-5 bg-soar-teal text-soar-blue rounded-2xl p-5 shadow-lg">
                <p className="font-heading text-3xl">9</p>
                <p className="text-sm">Jobs Secured</p>
              </div>
            </div>
            <div className="order-1 lg:order-2 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-soar-gold/15 flex items-center justify-center">
                  <Briefcase className="w-8 h-8 text-soar-gold" aria-hidden="true" />
                </div>
                <div>
                  <h2 className="font-heading text-4xl text-soar-blue tracking-tight">Resume & Interview Prep</h2>
                  <p className="font-accent text-2xl text-soar-gold">Career Readiness</p>
                </div>
              </div>
              <p className="text-lg text-soar-blue/60 leading-relaxed">
                Helps youth craft strong resumes and sharpen interview skills. Through hands-on guidance
                and mock interviews, youth learn to showcase their strengths.
              </p>
              <div className="card-elevated rounded-2xl p-6">
                <h4 className="font-heading text-soar-blue mb-4 text-sm">What You'll Gain:</h4>
                <ul className="space-y-3">
                  {[
                    { icon: FileText, text: "Receive personalized resume feedback" },
                    { icon: Users, text: "Prepare for real-world opportunities" },
                    { icon: Sparkles, text: "Build confidence for job interviews" },
                  ].map((item) => (
                    <li key={item.text} className="flex items-start gap-3">
                      <item.icon className="w-4 h-4 text-soar-gold flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="text-soar-blue/50 text-sm">{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Link to="/register?event=resume-interview&title=Resume%20Building%20%26%20Interview%20Prep">
                <Button className="rounded-full bg-soar-gold hover:bg-soar-gold/90 text-[#0a0a2e] px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                  Sign Up <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Create Your Lane */}
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-soar-teal/10 flex items-center justify-center">
                  <Palette className="w-8 h-8 text-soar-teal" aria-hidden="true" />
                </div>
                <div>
                  <h2 className="font-heading text-4xl text-soar-blue tracking-tight">Create Your Lane</h2>
                  <p className="font-accent text-2xl text-soar-gold">Entrepreneurship & Design</p>
                </div>
              </div>
              <p className="text-lg text-soar-blue/60 leading-relaxed">
                Learn creative and entrepreneurial skills to build your own brand and business. Get hands-on
                experience with professional tools.
              </p>
              <div className="card-elevated rounded-2xl p-6">
                <h4 className="font-heading text-soar-blue mb-4 text-sm">What You'll Learn:</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {["Design graphics using Canva", "Create products with Cricut", "Press apparel with Heat Press", "Basic copywriting skills", "Confidence in creativity", "Branding & entrepreneurship"].map((item) => (
                    <div key={item} className="flex items-start gap-2">
                      <Lightbulb className="w-3.5 h-3.5 text-soar-teal flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <span className="text-xs text-soar-blue/50">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <Link to="/register?event=evt-create-your-lane-2026&title=Create%20Your%20Lane">
                <Button className="rounded-full bg-soar-teal hover:bg-soar-teal/90 px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                  Sign Up <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img src="https://customer-assets.emergentagent.com/job_soaryouth/artifacts/lp0f2vc0_IMG_9689.jpeg" alt="Create Your Lane" className="rounded-3xl w-full" />
              <div className="absolute -bottom-5 -left-5 bg-white border border-soar-blue/10 text-soar-blue rounded-2xl p-5 shadow-lg">
                <Palette className="w-8 h-8 text-soar-gold mb-1" aria-hidden="true" />
                <p className="text-sm font-medium">Create Your Brand</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 relative overflow-hidden" data-testid="workshops-cta">
        <div className="absolute inset-0 bg-soar-gold" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-5xl md:text-6xl text-[#0a0a2e] tracking-tight mb-6">Ready to Build Your Skills?</h2>
          <p className="text-[#0a0a2e]/60 mb-10 max-w-2xl mx-auto text-lg">
            Our workshops are designed to give you practical, hands-on experience that prepares
            you for real-world success. Sign up today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="workshops-contact-btn">
              <Button className="rounded-full bg-white hover:bg-white/90 text-soar-blue px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                Sign Up for a Workshop
              </Button>
            </Link>
            <Link to="/mentorship-form" data-testid="workshops-mentorship-btn">
              <Button className="rounded-full bg-soar-blue/20 hover:bg-white/30 text-[#0a0a2e] border border-[#0a0a2e]/20 px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                Join Mentorship Program
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Workshops;
